import Editor from "@monaco-editor/react"
import {
  Add as AddIcon,
  DataObject as ObjectIcon,
  Delete as DeleteIcon,
  FactCheck as EnumIcon,
  Numbers as NumberIcon,
  ShortText as StringIcon
} from "@mui/icons-material"
import LoadingButton from "@mui/lab/LoadingButton"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material"
import { IAiPromptsInput } from "@pages/main/scripts/prompts"
import React, { SyntheticEvent, useEffect, useState } from "react"
import type { UseFormSetValue } from "react-hook-form"

export interface BaseResponseSchema {
  type: string
  required?: string[]
  properties?: Record<string, BaseResponseSchema>
  items?: BaseResponseSchema
  enum?: string[]
  description?: string
}

const TYPE_OPTIONS = [
  { value: "string", label: "String", icon: <StringIcon fontSize="small" /> },
  { value: "number", label: "Number", icon: <NumberIcon fontSize="small" /> },
  { value: "integer", label: "Integer", icon: <NumberIcon fontSize="small" /> },
  { value: "boolean", label: "Boolean", icon: <NumberIcon fontSize="small" /> },
  { value: "object", label: "Object", icon: <ObjectIcon fontSize="small" /> },
  { value: "array", label: "Array", icon: <NumberIcon fontSize="small" /> },
  { value: "enum", label: "Enum", icon: <EnumIcon fontSize="small" /> }
]

const renameObjectKey = <T,>(
  obj: Record<string, T>,
  oldKey: string,
  newKey: string,
  newValue: T
): Record<string, T> => {
  const keys = Object.keys(obj)
  const newObj: Record<string, T> = {}

  keys.forEach((k) => {
    if (k === oldKey) {
      newObj[newKey] = newValue
    } else {
      newObj[k] = obj[k]
    }
  })

  return newObj
}

interface SchemaNodeEditorProps {
  node: BaseResponseSchema
  fieldKey: string
  onChange: (newNode: BaseResponseSchema, newKey?: string) => void
  onDelete?: () => void
  isRoot?: boolean
  parentType?: "object" | "array"
}

const SchemaNodeEditor: React.FC<SchemaNodeEditorProps> = ({
  node,
  fieldKey,
  onChange,
  onDelete,
  isRoot = false,
  parentType
}) => {
  const getUiType = (): string => {
    if (node.enum) return "enum"
    return node.type || "string"
  }

  const handleTypeChange = (newUiType: string) => {
    const base: BaseResponseSchema = {
      type: newUiType,
      description: node.description
    }

    if (newUiType === "object") {
      base.type = "object"
      base.properties = {}
      base.required = []
    } else if (newUiType === "array") {
      base.type = "array"
      base.items = { type: "string" }
    } else if (newUiType === "enum") {
      base.type = "string"
      base.enum = []
    } else {
      base.type = newUiType
    }
    onChange(base, fieldKey)
  }

  const renderObjectChildren = () => {
    const props = node.properties || {}
    const entries = Object.entries(props)

    return (
      <Box sx={{ pl: 2, mt: 1, borderLeft: "2px solid #eee" }}>
        {entries.map(([childKey, childNode], index) => (
          <Box key={index} sx={{ mb: 2 }}>
            {" "}
            <SchemaNodeEditor
              node={childNode}
              fieldKey={childKey}
              parentType="object"
              onDelete={() => {
                const newProps = { ...props }
                delete newProps[childKey]
                const newRequired = (node.required || []).filter(
                  (k) => k !== childKey
                )
                onChange(
                  { ...node, properties: newProps, required: newRequired },
                  fieldKey
                )
              }}
              onChange={(updatedNode, updatedKey) => {
                if (updatedKey && updatedKey !== childKey) {
                  const newProps = renameObjectKey(
                    props,
                    childKey,
                    updatedKey,
                    updatedNode
                  )

                  const reqIndex = (node.required || []).indexOf(childKey)
                  const newRequired = [...(node.required || [])]
                  if (reqIndex >= 0) newRequired[reqIndex] = updatedKey

                  onChange(
                    { ...node, properties: newProps, required: newRequired },
                    fieldKey
                  )
                } else {
                  const newProps = { ...props, [childKey]: updatedNode }
                  onChange({ ...node, properties: newProps }, fieldKey)
                }
              }}
            />
          </Box>
        ))}
        <Button
          startIcon={<AddIcon />}
          size="small"
          onClick={() => {
            const newKey = `field_${entries.length + 1}`
            onChange(
              {
                ...node,
                properties: { ...props, [newKey]: { type: "string" } },
                required: [...(node.required || []), newKey]
              },
              fieldKey
            )
          }}
        >
          Add Property
        </Button>
      </Box>
    )
  }

  const renderArrayItem = () => {
    if (!node.items) return null
    return (
      <Box sx={{ pl: 2, mt: 1, borderLeft: "2px solid #e3f2fd" }}>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", mb: 0.5, display: "block" }}
        >
          Items Schema:
        </Typography>
        <SchemaNodeEditor
          node={node.items}
          fieldKey=""
          parentType="array"
          onChange={(newNode) =>
            onChange({ ...node, items: newNode }, fieldKey)
          }
        />
      </Box>
    )
  }

  const renderEnum = () => {
    return (
      <Box sx={{ mt: 1 }}>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={node.enum || []}
          onChange={(_, newValue) => {
            onChange({ ...node, enum: newValue as string[] }, fieldKey)
          }}
          renderTags={(value: ReadonlyArray<string>, getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip
                //@ts-expect-error asd
                key={index}
                variant="outlined"
                label={option}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Allowed Values"
              placeholder="Type and enter"
              size="small"
            />
          )}
        />
      </Box>
    )
  }

  const uiType = getUiType()
  const isKeyEditable = parentType === "object"

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
    >
      <Stack direction="row" spacing={1} alignItems="start">
        {isKeyEditable && (
          <TextField
            label="Key"
            size="small"
            value={fieldKey}
            onChange={(e) => onChange(node, e.target.value)}
            sx={{ width: 150, flexShrink: 0 }}
            error={fieldKey === ""}
          />
        )}

        <TextField
          select
          label={isRoot ? "Root Type" : "Type"}
          size="small"
          value={uiType}
          onChange={(e) => handleTypeChange(e.target.value)}
          sx={{ width: 130, flexShrink: 0 }}
          disabled={isRoot && uiType === "object"}
        >
          {TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Stack direction="row" spacing={1} alignItems="center">
                {opt.icon} <Typography variant="body2">{opt.label}</Typography>
              </Stack>
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Description"
          size="small"
          fullWidth
          value={node.description || ""}
          onChange={(e) =>
            onChange({ ...node, description: e.target.value }, fieldKey)
          }
        />

        {!isRoot && onDelete && (
          <IconButton color="error" size="small" onClick={onDelete}>
            <DeleteIcon />
          </IconButton>
        )}
      </Stack>

      {uiType === "object" && renderObjectChildren()}
      {uiType === "array" && renderArrayItem()}
      {uiType === "enum" && renderEnum()}
    </Box>
  )
}

interface ResponseSchemaBuilderProps {
  open: boolean
  setOpen: (open: boolean) => void
  setValue: UseFormSetValue<IAiPromptsInput>
  initialValue?: BaseResponseSchema | null
}

const DEFAULT_ROOT_SCHEMA: BaseResponseSchema = {
  type: "object",
  properties: {},
  required: []
}

export default function ResponseSchemaBuilder({
  open,
  setOpen,
  setValue,
  initialValue
}: ResponseSchemaBuilderProps) {
  const [tabValue, setTabValue] = useState(0)
  const [jsonDraft, setJsonDraft] = useState<string>("")
  const [error, setError] = useState<string | null>(null)

  const [rootSchema, setRootSchema] =
    useState<BaseResponseSchema>(DEFAULT_ROOT_SCHEMA)

  useEffect(() => {
    if (open) {
      const startVal = initialValue || DEFAULT_ROOT_SCHEMA
      setRootSchema(startVal)

      setJsonDraft(JSON.stringify(startVal, null, 2))
      setError(null)
    }
  }, [open, initialValue])

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    if (newValue === 1) {
      setJsonDraft(JSON.stringify(rootSchema, null, 2))
    } else {
      try {
        const obj: BaseResponseSchema = JSON.parse(jsonDraft)
        // Basic validation check
        if (!obj.type) throw new Error("Invalid structure: missing type")
        setRootSchema(obj)
        setError(null)
      } catch (e) {
        setError(
          "Invalid JSON, cannot switch to Visual Builder. Fix errors first."
        )
        return
      }
    }
    setTabValue(newValue)
  }

  const handleSave = () => {
    try {
      let finalObj: BaseResponseSchema
      if (tabValue === 0) {
        finalObj = rootSchema
      } else {
        finalObj = JSON.parse(jsonDraft)
      }

      setValue("responseSchema", finalObj, {
        shouldValidate: true,
        shouldDirty: true
      })
      setOpen(false)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
      <DialogTitle sx={{ p: 0 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
          <Tab label="Visual Schema Builder" />
          <Tab label="Raw JSON Editor" />
        </Tabs>
      </DialogTitle>

      <DialogContent dividers sx={{ height: 600, p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 1 }}>
            {error}
          </Alert>
        )}

        {tabValue === 0 ? (
          <Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
            <Stack spacing={2} sx={{ mb: 3 }}>
              <Divider />

              <Typography variant="h6">Response Schema</Typography>
              <SchemaNodeEditor
                isRoot
                fieldKey="root"
                node={rootSchema}
                onChange={(newNode) => setRootSchema(newNode)}
              />
            </Stack>
          </Box>
        ) : (
          <Editor
            height="100%"
            defaultLanguage="json"
            value={jsonDraft}
            onChange={(v) => {
              setJsonDraft(v || "{}")
              setError(null)
            }}
            options={{
              minimap: { enabled: false },
              formatOnPaste: true,
              tabSize: 2
            }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <LoadingButton variant="contained" onClick={handleSave}>
          Save Schema
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
